import React from 'react';

/*
 * One icon family for the whole site: 24px grid, 1.6px stroke, round caps.
 * Everything inherits currentColor so icons follow the text they sit beside.
 */

type IconProps = {
  size?: number | string;
  className?: string;
  strokeWidth?: number;
};

const base = (size: number | string, className?: string, strokeWidth = 1.6) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className,
  'aria-hidden': true,
  focusable: false,
});

export const Search = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <circle cx='11' cy='11' r='7' />
    <path d='m20 20-3.6-3.6' />
  </svg>
);

export const User = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <circle cx='12' cy='8' r='4' />
    <path d='M4.5 20a7.5 7.5 0 0 1 15 0' />
  </svg>
);

export const PhoneIcon = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M6.5 3.5h-2A1.5 1.5 0 0 0 3 5.06C3.36 12.4 9.6 18.64 16.94 19a1.5 1.5 0 0 0 1.56-1.5v-2a1.5 1.5 0 0 0-1.2-1.47l-2.1-.42a1.5 1.5 0 0 0-1.5.62l-.7.98a11.6 11.6 0 0 1-5.21-5.21l.98-.7a1.5 1.5 0 0 0 .62-1.5l-.42-2.1A1.5 1.5 0 0 0 6.5 3.5Z' />
  </svg>
);

export const Mail = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <rect x='3' y='5' width='18' height='14' rx='2.5' />
    <path d='m3.8 6.8 7.2 5.2a1.7 1.7 0 0 0 2 0l7.2-5.2' />
  </svg>
);

export const MapPin = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M12 21c4.2-4.2 6.3-7.5 6.3-10a6.3 6.3 0 1 0-12.6 0c0 2.5 2.1 5.8 6.3 10Z' />
    <circle cx='12' cy='10.6' r='2.3' />
  </svg>
);

export const ChevronDown = ({ size = 16, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='m6 9.5 6 5.5 6-5.5' />
  </svg>
);

export const ChevronRight = ({ size = 16, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='m9 5.5 6.5 6.5L9 18.5' />
  </svg>
);

export const ChevronLeft = ({ size = 16, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M15 5.5 8.5 12 15 18.5' />
  </svg>
);

export const ArrowRight = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M4 12h15' />
    <path d='m13.5 6 6 6-6 6' />
  </svg>
);

export const ArrowLeft = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M20 12H5' />
    <path d='m10.5 6-6 6 6 6' />
  </svg>
);

export const Menu = ({ size = 22, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M3.5 7h17M3.5 12h17M3.5 17h11' />
  </svg>
);

export const Close = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M6 6l12 12M18 6 6 18' />
  </svg>
);

export const Gift = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M3.5 11.5h17V19a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2v-7.5Z' />
    <rect x='2.5' y='7.5' width='19' height='4' rx='1.2' />
    <path d='M12 7.5V21' />
    <path d='M12 7.5S11 3 8.5 3a2.25 2.25 0 0 0 0 4.5H12Zm0 0s1-4.5 3.5-4.5a2.25 2.25 0 0 1 0 4.5H12Z' />
  </svg>
);

export const Calendar = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <rect x='3' y='5' width='18' height='16' rx='2.5' />
    <path d='M3 10h18M8 3v4M16 3v4' />
  </svg>
);

export const Plus = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M12 5v14M5 12h14' />
  </svg>
);

export const Minus = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M5 12h14' />
  </svg>
);

export const Check = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='m4.5 12.5 5 5 10-11' />
  </svg>
);

export const Sliders = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M4 7h10M18 7h2M4 17h4M12 17h8' />
    <circle cx='16' cy='7' r='2' />
    <circle cx='10' cy='17' r='2' />
  </svg>
);

export const Grid = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <rect x='3.5' y='3.5' width='7' height='7' rx='1.6' />
    <rect x='13.5' y='3.5' width='7' height='7' rx='1.6' />
    <rect x='3.5' y='13.5' width='7' height='7' rx='1.6' />
    <rect x='13.5' y='13.5' width='7' height='7' rx='1.6' />
  </svg>
);

export const Rows = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <rect x='3.5' y='4.5' width='17' height='6' rx='1.6' />
    <rect x='3.5' y='13.5' width='17' height='6' rx='1.6' />
  </svg>
);

export const Heart = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M12 20s-7.5-4.6-7.5-9.4A4.1 4.1 0 0 1 12 8a4.1 4.1 0 0 1 7.5 2.6C19.5 15.4 12 20 12 20Z' />
  </svg>
);

export const Copy = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <rect x='9' y='9' width='12' height='12' rx='2.4' />
    <path d='M15 5.8A2.8 2.8 0 0 0 12.2 3H5.8A2.8 2.8 0 0 0 3 5.8v6.4A2.8 2.8 0 0 0 5.8 15' />
  </svg>
);

export const Download = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M12 3.5v12' />
    <path d='m7.5 11 4.5 4.5 4.5-4.5' />
    <path d='M4.5 20h15' />
  </svg>
);

export const Info = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 11v5.5M12 7.8v.4' />
  </svg>
);

export const Spark = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M12 3.5c0 4 1.5 5.5 5.5 5.5-4 0-5.5 1.5-5.5 5.5 0-4-1.5-5.5-5.5-5.5 4 0 5.5-1.5 5.5-5.5Z' />
    <path d='M18 15.5c0 2 .8 2.8 2.8 2.8-2 0-2.8.8-2.8 2.8 0-2-.8-2.8-2.8-2.8 2 0 2.8-.8 2.8-2.8Z' />
  </svg>
);

export const Send = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M20.5 3.5 10.8 13.2' />
    <path d='M20.5 3.5 14.3 20.5l-3.5-7.3-7.3-3.5 17-6.2Z' />
  </svg>
);

export const Store = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M4 10v9a1.5 1.5 0 0 0 1.5 1.5h13A1.5 1.5 0 0 0 20 19v-9' />
    <path d='M3 6.5 4.4 3.8A1.5 1.5 0 0 1 5.7 3h12.6a1.5 1.5 0 0 1 1.3.8L21 6.5a3 3 0 0 1-5.6 1.6A3 3 0 0 1 12 9.6 3 3 0 0 1 8.6 8.1 3 3 0 0 1 3 6.5Z' />
  </svg>
);

export const Shield = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M12 21c4.5-2 6.5-5.2 6.5-9.5V5.8L12 3 5.5 5.8v5.7C5.5 15.8 7.5 19 12 21Z' />
    <path d='m9.3 11.8 2 2 3.4-3.6' />
  </svg>
);

export const Clock = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 7v5.3l3.2 2' />
  </svg>
);

export const Logout = ({ size = 18, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M14 4.5h3.5A1.5 1.5 0 0 1 19 6v12a1.5 1.5 0 0 1-1.5 1.5H14' />
    <path d='M10 8.5 6.5 12 10 15.5' />
    <path d='M6.5 12H15' />
  </svg>
);

export const Chat = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <path d='M20.5 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.9A8 8 0 1 1 20.5 12Z' />
    <path d='M9 11.5h6M9 14.5h3.5' />
  </svg>
);

export const Globe = ({ size = 20, className, strokeWidth }: IconProps) => (
  <svg {...base(size, className, strokeWidth)}>
    <circle cx='12' cy='12' r='9' />
    <path d='M3.4 9.5h17.2M3.4 14.5h17.2' />
    <path d='M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z' />
  </svg>
);
